import { useCallback, useState } from "react";
import type { Device, View } from "@gooner/shared";
import { api } from "../../lib/api";
import { ViewPreview } from "./ViewPreview";
import { CardShell, FooterAction } from "./CardShell";
import { IconMonitor, IconPencil, IconTrash } from "../ui/icons";
import { openScreenTab } from "../lib/playView";
import { viewGrid } from "../lib/format";

/**
 * One view in the library.
 *
 * **Clicking it plays it**: a device is minted and `/view/d/:id` opens in a new tab, with
 * no code and no QR because the controller already knows who you are. **Editing costs a
 * hover.** That is the inversion of the document-library convention, taken deliberately —
 * this is a control surface, the common act is putting a view on a screen, and the
 * frequent act gets the whole card.
 *
 * The shell, the badge corners and the hover-action slot are CollectionCard's. The five
 * documented differences are marked at the lines that make them.
 *
 * The card plays a RENDERED preview, not the view itself. Mounting the view live cost a
 * source fetch and a decoder per region — a library of eight four-region views was
 * thirty-odd of each — and the wall of moving previews the library is meant to be costs
 * one file per view instead. See ViewPreview.
 */
export function ViewCard({
  view,
  onScreens,
  onEdit,
  onDelete,
  onSendTo,
  onOpened,
}: {
  view: View;
  onScreens: number;
  onEdit: () => void;
  onDelete: () => void;
  onSendTo: (anchor: HTMLElement) => void;
  onOpened: (device: Device) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [blockedUrl, setBlockedUrl] = useState<string | null>(null);

  /** Difference 4: the body is this, not a Link to a detail page. */
  const play = useCallback(() => {
    // Synchronous, before any await — see playView.ts.
    const tab = openScreenTab();
    setBusy(true);
    setBlockedUrl(null);
    void api.devices
      .open({ viewId: view.id, kind: "monitor" })
      .then(({ device }) => {
        tab.settle(device.id);
        if (tab.blocked) setBlockedUrl(`/view/d/${device.id}`);
        onOpened(device);
      })
      .catch(() => tab.settle(null))
      .finally(() => setBusy(false));
  }, [view.id, onOpened]);

  const grid = viewGrid(view.regions);
  const n = view.regions.length;
  const geometry = `${grid ? `${grid} · ` : ""}${n} ${n === 1 ? "region" : "regions"}`;

  return (
    <CardShell
      busy={busy}
      onPlay={play}
      playLabel={`Play ${view.name} on a new screen`}
      media={<ViewPreview viewId={view.id} name={view.name} regionCount={n} />}
      /*
        The device count. Green because it means LIVE, over the preview because it
        describes what is happening TO the view rather than what the view is.

        Absent at zero, deliberately: a view nobody is watching must not carry a `0`. The
        pill exists to say "this is on screens right now", and a zero states the ordinary
        case loudly. Same corner CollectionCard gives Editor's Choice.
      */
      pill={
        onScreens > 0 ? (
          <span
            title={`On ${onScreens} ${onScreens === 1 ? "screen" : "screens"} right now`}
            aria-label={`On ${onScreens} ${onScreens === 1 ? "screen" : "screens"} right now`}
            className="rounded bg-emerald-500/90 px-1.5 py-0.5 text-[10px] font-semibold text-white"
          >
            {onScreens}
          </span>
        ) : null
      }
      badge={geometry}
      name={view.name}
      icon={view.icon || "🖥️"}
      /*
        One footer row holding everything you can do to the view, in the same place and the
        same style on every card on this screen. Edit used to sit alone in the corner hover
        slot, which read as decoration rather than as the button it is — the operator
        looked at the card and reported there was no Edit on it.

        Still a Link, so middle-click and open-in-new-tab reach the editor; the card body
        cannot offer that any more, because it is an action rather than a link.

        There is no Duplicate here. Copying a view you already own produces a second thing
        you have to tell apart from the first, and the operation people actually reach for
        — "start from this shape" — is what the quick setups' Copy is for. It stays on
        THAT card, where the thing being copied is not yours yet.

        Each label names the view, so each is masked in replay (`mask`); the buttons are
        icon-only, so the replay still shows three buttons in the same place.
      */
      footerActions={
        <>
          <FooterAction
            label={`Edit ${view.name} in the layout editor`}
            to={`/views/${view.id}`}
            mask
            onClick={onEdit}
          >
            <IconPencil width={13} height={13} />
          </FooterAction>
          <FooterAction label={`Send ${view.name} to a screen`} mask onClick={onSendTo}>
            <IconMonitor width={13} height={13} />
          </FooterAction>
          <FooterAction label={`Delete ${view.name}`} danger mask onClick={() => onDelete()}>
            <IconTrash width={13} height={13} />
          </FooterAction>
        </>
      }
      blockedUrl={blockedUrl}
    />
  );
}
